/**
 * Blog post page — fetches the Markdown body from /content/blog/{slug}.md
 * and renders it with react-markdown (+ GFM). Injects per-post SEO tags:
 *   - <title>, meta description, keywords
 *   - canonical + hreflang to sibling-lang post
 *   - Open Graph (article) + Twitter card
 *   - JSON-LD BlogPosting structured data
 *
 * The body comes with YAML front-matter that we strip client-side (the
 * manifest already carries the metadata so we trust it as the source of
 * truth and just render the body).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug, POSTS } from '@/content/blogManifest';
import DirectInstallButton from '@/components/DirectInstallButton';
import { ArrowLeft, BookOpen, Calendar, User } from 'lucide-react';

const SITE = 'https://playmasteria.com';

function applySeo(post, sibling) {
  if (typeof document === 'undefined') return;
  const url = `${SITE}/blog/${post.slug}`;
  // Dynamic OG card — auto-generated 1200×630 PNG with the post's title,
  // language and brand stamp (backend /api/share/blog/{slug}/og.png).
  const ogImage = `${SITE}/api/share/blog/${post.slug}/og.png`;
  document.title = `${post.title} | PlayMaster IA Blog`;

  const tags = [
    { sel: 'meta[name="description"]', attrs: { name: 'description', content: post.description } },
    { sel: 'meta[name="keywords"]',    attrs: { name: 'keywords',    content: post.keywords } },
    { sel: 'meta[property="og:type"]',        attrs: { property: 'og:type', content: 'article' } },
    { sel: 'meta[property="og:title"]',       attrs: { property: 'og:title', content: post.title } },
    { sel: 'meta[property="og:description"]', attrs: { property: 'og:description', content: post.description } },
    { sel: 'meta[property="og:url"]',         attrs: { property: 'og:url', content: url } },
    { sel: 'meta[property="og:image"]',       attrs: { property: 'og:image', content: ogImage } },
    { sel: 'meta[property="og:image:width"]', attrs: { property: 'og:image:width', content: '1200' } },
    { sel: 'meta[property="og:image:height"]',attrs: { property: 'og:image:height', content: '630' } },
    { sel: 'meta[property="article:published_time"]', attrs: { property: 'article:published_time', content: post.date } },
    { sel: 'meta[name="twitter:card"]',        attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { sel: 'meta[name="twitter:title"]',       attrs: { name: 'twitter:title', content: post.title } },
    { sel: 'meta[name="twitter:description"]', attrs: { name: 'twitter:description', content: post.description } },
    { sel: 'meta[name="twitter:image"]',       attrs: { name: 'twitter:image', content: ogImage } },
  ];
  tags.forEach(({ sel, attrs }) => {
    let el = document.head.querySelector(sel);
    if (!el) {
      el = document.createElement('meta');
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  });

  // Canonical
  let canon = document.head.querySelector('link[rel="canonical"]');
  if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
  canon.href = url;

  // hreflang (sibling-lang post if it exists)
  document.head.querySelectorAll('link[data-pm-alt]').forEach(n => n.remove());
  const addAlt = (lang, href) => {
    const l = document.createElement('link');
    l.rel = 'alternate';
    l.hreflang = lang;
    l.href = href;
    l.setAttribute('data-pm-alt', '1');
    document.head.appendChild(l);
  };
  addAlt(post.lang, url);
  if (sibling) addAlt(sibling.lang, `${SITE}/blog/${sibling.slug}`);
  addAlt('x-default', url);

  // JSON-LD BlogPosting
  let ld = document.getElementById('pm-post-jsonld');
  if (!ld) { ld = document.createElement('script'); ld.type = 'application/ld+json'; ld.id = 'pm-post-jsonld'; document.head.appendChild(ld); }
  ld.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    headline: post.title,
    description: post.description,
    keywords: post.keywords,
    inLanguage: post.lang,
    datePublished: post.date,
    dateModified: post.date,
    image: ogImage,
    author: { '@type': 'Organization', name: post.author || 'PlayMaster IA Team', url: SITE },
    publisher: {
      '@type': 'Organization',
      name: 'PlayMaster IA',
      url: SITE,
      logo: { '@type': 'ImageObject', url: `${SITE}/icon-512.png` },
    },
  });
}

const MD_COMPONENTS = {
  h1: (p) => <h2 className="font-h uppercase text-2xl text-gold mt-8 mb-3" {...p} />,
  h2: (p) => <h2 className="font-h uppercase text-2xl text-gold mt-8 mb-3" {...p} />,
  h3: (p) => <h3 className="font-h uppercase text-lg text-white mt-6 mb-2" {...p} />,
  p:  (p) => <p  className="text-gray-200 leading-relaxed mb-4 text-base" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-1.5 mb-4 text-gray-200" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-1.5 mb-4 text-gray-200" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  a:  (p) => <a className="text-gold hover:underline" {...p} />,
  strong: (p) => <strong className="text-white" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-gold pl-4 my-4 text-gray-300 italic" {...p} />,
  code: (p) => <code className="bg-card-steel border border-gold/20 px-1.5 py-0.5 rounded text-gold text-sm" {...p} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);

  // Find a sibling post in the other language to provide hreflang alternate.
  // We match by index within each language (best-effort) so e.g. the 1st EN
  // post links to the 1st ES post.
  const sibling = (() => {
    if (!post) return null;
    const sameLang = POSTS.filter(p => p.lang === post.lang);
    const idx = sameLang.findIndex(p => p.slug === post.slug);
    const otherLang = POSTS.filter(p => p.lang !== post.lang);
    return otherLang[idx] || null;
  })();

  useEffect(() => {
    if (!post) return;
    applySeo(post, sibling);
    setLoading(true);
    fetch(`/content/blog/${post.slug}.md`)
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(text => {
        // Strip YAML front-matter.
        const stripped = text.replace(/^---[\s\S]*?---\n+/, '');
        setBody(stripped);
      })
      .catch(() => setBody(''))
      .finally(() => setLoading(false));
  }, [post, sibling]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <main className="min-h-screen bg-navy-deep text-white px-4 sm:px-8 py-8" data-testid={`blog-post-${post.slug}`}>
      <article className="max-w-3xl mx-auto">
        <Link
          to="/blog"
          data-testid="blog-back"
          className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-6 text-xs font-mono uppercase tracking-[0.3em]"
        >
          <ArrowLeft className="w-4 h-4" /> {post.lang === 'es' ? 'Volver al blog' : 'Back to blog'}
        </Link>

        <header className="mb-6 pb-6 border-b border-gold/20">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 mb-2 flex items-center gap-3 flex-wrap">
            <span>{post.lang === 'es' ? 'Español' : 'English'}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{post.date}</span>
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{post.author || 'PlayMaster IA Team'}</span>
          </div>
          <h1 className="font-h uppercase text-3xl sm:text-4xl text-white leading-tight">{post.title}</h1>
          <p className="text-gray-300 text-sm mt-2">{post.description}</p>
          <div className="mt-4">
            <DirectInstallButton variant="primary" lang={post.lang} />
          </div>
        </header>

        {loading ? (
          <div className="text-gray-400 py-10">{post.lang === 'es' ? 'Cargando…' : 'Loading…'}</div>
        ) : (
          <div className="prose-pm" data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {body}
            </ReactMarkdown>
          </div>
        )}

        {/* Footer CTA */}
        <section className="mt-12 p-6 bg-gradient-to-r from-gold/10 to-card-steel border-2 border-gold/40 rounded-xl text-center" data-testid="blog-post-cta">
          <BookOpen className="w-8 h-8 text-gold mx-auto mb-2" />
          <h3 className="font-h uppercase text-xl mb-2">
            {post.lang === 'es' ? 'Probá Basketball Playbook #1' : 'Try Basketball Playbook #1'}
          </h3>
          <p className="text-sm text-gray-300 mb-4 max-w-xl mx-auto">
            {post.lang === 'es'
              ? 'La app más fácil, rápida e inteligente para coaches. Instalación directa.'
              : 'The easiest, fastest and smartest app for coaches. Direct install.'}
          </p>
          <DirectInstallButton variant="primary" lang={post.lang} />
        </section>

        {/* Related posts */}
        <section className="mt-12" data-testid="blog-related">
          <h3 className="font-h uppercase text-lg text-gold mb-3">
            {post.lang === 'es' ? 'Más artículos' : 'More articles'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {POSTS.filter(p => p.lang === post.lang && p.slug !== post.slug).slice(0, 4).map(p => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="block bg-card-steel border border-gold/20 hover:border-gold rounded-lg p-4 transition">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 mb-1">{p.date}</div>
                <div className="font-h text-base text-white">{p.title}</div>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </main>
  );
}
